import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import clipoidLogo from '../../assets/clipoid_logo.png';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';
import { useTurnstile } from '../hooks/useTurnstile';

// PKCE Helper functions
const generateRandomString = (length: number): string => {
  const array = new Uint32Array(length / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
};

const sha256 = async (plain: string): Promise<ArrayBuffer> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

const base64urlencode = (a: ArrayBuffer): string => {
  return btoa(String.fromCharCode.apply(null, new Uint8Array(a) as any))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const hashed = await sha256(verifier);
  return base64urlencode(hashed);
};

function RegisterPage() {
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSocialLoginLoading, setIsSocialLoginLoading] = useState<null | 'google' | 'twitch' | 'kick'>(null);
  const [error, setError] = useState<string | null>(null);
  
  const { 
    turnstileToken, 
    turnstileError, 
    turnstileWidgetRef, 
    setTurnstileError,
    resetTurnstile
  } = useTurnstile({ action: 'register' });

  const { register, isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();

  // Generate a unique session ID for this registration attempt
  const sessionId = React.useMemo(() => {
    return `register_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Track current popup for cleanup
  const currentPopupRef = React.useRef<Window | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const validateUsername = (username: string) => {
    if (!username) return 'Username is required.';
    if (username.length < 3) return 'Username must be at least 3 characters.';
    return '';
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email address is required.';
    if (!emailRegex.test(email)) return 'Enter a valid email address.';
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password) return 'Password is required.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    return '';
  };

  const validateConfirmPassword = (confirmPassword: string, password: string) => {
    if (!confirmPassword) return 'Password confirmation is required.';
    if (confirmPassword !== password) return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTurnstileError(null);

    const usernameValidationError = validateUsername(username);
    const emailValidationError = validateEmail(email);
    const passwordValidationError = validatePassword(password);
    const confirmPasswordValidationError = validateConfirmPassword(confirmPassword, password);

    setUsernameError(usernameValidationError);
    setEmailError(emailValidationError);
    setPasswordError(passwordValidationError);
    setConfirmPasswordError(confirmPasswordValidationError);
    setError(null);

    if (!turnstileToken) {
      setTurnstileError('Please complete the CAPTCHA verification.');
      turnstileWidgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!usernameValidationError && !emailValidationError && !passwordValidationError && !confirmPasswordValidationError) {
      setIsSubmitting(true);
      try {
        const result = await register(username, email, password, name || undefined, turnstileToken);
        if (!result.success) {
          setError(result.message || 'Registration failed.');
          resetTurnstile();
        }
      } catch (err) {
        setError('An error occurred during registration. Please try again later.');
        resetTurnstile();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'twitch' | 'kick') => {
    if (isSocialLoginLoading === provider) {
      return;
    }
    setIsSocialLoginLoading(provider);
    setError(null);
    setTurnstileError(null);

    if (!turnstileToken) {
      setTurnstileError('Please complete the CAPTCHA verification before proceeding.');
      if (turnstileWidgetRef.current) {
        turnstileWidgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setIsSocialLoginLoading(null);
      return;
    }

    try {
      // Close any existing popup
      if (currentPopupRef.current && !currentPopupRef.current.closed) {
        currentPopupRef.current.close();
        currentPopupRef.current = null;
      }

      if (provider === 'kick') {
        const codeVerifier = generateRandomString(64);
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem('kick_code_verifier', codeVerifier);

        const response = await api.post('/auth/kick', {
          code_challenge: codeChallenge,
          cfTurnstileResponse: turnstileToken,
          session_id: sessionId,
        });

        if (response.data.success && response.data.url) {
          // Use popup for Kick as well
          const width = 500;
          const height = 600;
          const left = (window.screen.width / 2) - (width / 2);
          const top = (window.screen.height / 2) - (height / 2);
          
          // Prepare popup URL with session ID and popup indicator (Kick)
          let popupUrl = response.data.url;
          if (sessionId) {
            const url = new URL(response.data.url);
            url.searchParams.set('session_id', sessionId);
            url.searchParams.set('popup', 'true');
            popupUrl = url.toString();
          }
          
          const popup = window.open(
            popupUrl,
            'kickAuth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=no,toolbar=no,menubar=no`
          );
          
          // Listen for postMessage - Production Secure Implementation
          const handleMessage = (event: MessageEvent) => {
            console.log('[RegisterPage Kick] Received message:', {
              origin: event.origin,
              type: event.data?.type,
              provider: event.data?.provider,
              sessionId: event.data?.sessionId,
              timestamp: event.data?.timestamp ? new Date(event.data.timestamp).toISOString() : 'No timestamp'
            });
            
            // Security: Skip non-OAuth messages
            if (event.data?.source === 'react-devtools-bridge') {
              return;
            }
            
            // Security: Strict origin validation
            if (event.origin !== window.location.origin) {
              console.warn('[RegisterPage Kick] SECURITY: Rejected message from untrusted origin:', event.origin);
              return;
            }
            
            // Security: Validate message structure
            if (!event.data?.type || !event.data?.provider || !event.data?.timestamp) {
              console.warn('[RegisterPage Kick] SECURITY: Invalid message structure');
              return;
            }
            
            // Security: Check message age (prevent replay attacks)
            if (event.data.timestamp && typeof event.data.timestamp === 'number') {
              const messageAge = Date.now() - event.data.timestamp;
              if (messageAge > 60000) { // 60 seconds max
                console.warn('[RegisterPage Kick] SECURITY: Message too old, ignoring');
                return;
              }
            } else {
              console.warn('[RegisterPage Kick] SECURITY: Invalid timestamp in message');
              return;
            }
            
            if (event.data.type === 'oauth-success' && event.data.provider === 'kick') {
              console.log('[RegisterPage Kick] OAuth success - processing');
              
              // Force close popup immediately
              if (popup && !popup.closed) {
                try {
                  popup.postMessage({ type: 'force-close' }, window.location.origin);
                  popup.close();
                  console.log('[RegisterPage Kick] Popup closed successfully');
                } catch (error) {
                  console.warn('[RegisterPage Kick] Popup close failed:', error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              
              // BUGFIX: Send confirmation to popup before navigation
              if (popup && !popup.closed) {
                try {
                  popup.postMessage({ type: 'auth-processed' }, window.location.origin);
                } catch (error) {
                  console.warn('[RegisterPage] Failed to send auth-processed message:', error);
                }
              }
              
              navigate('/dashboard');
            } else if (event.data.type === 'oauth-error') {
              console.log('[RegisterPage Kick] OAuth error:', event.data.error);
              
              if (popup && !popup.closed) {
                try {
                  popup.close();
                } catch (error) {
                  console.warn('[RegisterPage Kick] Error popup close failed:', error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              setError(event.data.error || 'Kick registration failed');
            }
          };
          
          window.addEventListener('message', handleMessage);
          
          // No server polling needed - using postMessage only
          let pollInterval: NodeJS.Timeout | null = null;
          
          // Cleanup function
          const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (pollInterval) clearInterval(pollInterval);
            if (checkClosed) clearInterval(checkClosed);
          };
          
          // Monitor popup closing and check auth status (Kick)
          let checkClosed: NodeJS.Timeout | null = null;
          
          const monitorKickPopupClose = () => {
            checkClosed = setInterval(async () => {
              try {
                if (popup && popup.closed) {
                  console.log('[RegisterPage] 🔍 Kick popup closed detected, checking auth status...');
                  clearInterval(checkClosed!);
                  
                  // Wait for cookies to be available
                  setTimeout(async () => {
                    try {
                      // Check if user is now authenticated
                      await checkAuth();
                      console.log('[RegisterPage] ✅ Kick auth check completed, redirecting...');
                      cleanup();
                      setIsSocialLoginLoading(null);
                      window.location.href = '/dashboard';
                    } catch (authError) {
                      console.error('[RegisterPage] ❌ Kick auth check failed:', authError);
                      setIsSocialLoginLoading(null);
                      setError('Kick registration failed. Please try again.');
                    }
                  }, 500); // 500ms delay for cookies
                }
              } catch (error) {
                console.warn('[RegisterPage] Error checking Kick popup status:', error);
              }
            }, 250); // Check every 250ms
          };
          
          // Start monitoring Kick popup close
          monitorKickPopupClose();
        } else {
           throw new Error(response.data.message || 'Failed to get Kick auth URL.');
        }
      } else {
        // For OAuth providers, make request to get redirect URL
        const response = await api.get(`/auth/${provider}`, {
          params: {
            session_id: sessionId,
            popup: 'true',
            turnstile: turnstileToken || ''
          }
        });

        if (response.data.success && response.data.url) {
          // Use popup for all social logins
          // Calculate center position
          const width = 500;
          const height = 600;
          const left = (window.screen.width / 2) - (width / 2);
          const top = (window.screen.height / 2) - (height / 2);
          
          // Open OAuth in centered popup window with actual OAuth URL
          const popup = window.open(
            response.data.url,
            `${provider}Auth`,
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,status=yes,location=no,toolbar=no,menubar=no`
          );

        if (popup) {
          
          // Listen for postMessage (primary method) - Production Secure Implementation
          const handleMessage = async (event: MessageEvent) => {
            console.log('[RegisterPage] ===== RECEIVED MESSAGE =====');
            console.log('[RegisterPage] Event source window === popup:', event.source === popup);
            console.log('[RegisterPage] Event origin:', event.origin);
            console.log('[RegisterPage] Current origin:', window.location.origin);
            console.log('[RegisterPage] Full event data:', event.data);
            console.log('[RegisterPage] Message details:', {
              origin: event.origin,
              type: event.data?.type,
              provider: event.data?.provider,
              sessionId: event.data?.sessionId,
              timestamp: event.data?.timestamp ? new Date(event.data.timestamp).toISOString() : 'No timestamp'
            });
            
            // Security: Skip non-OAuth messages
            if (event.data?.source === 'react-devtools-bridge' || 
                event.data?.source === 'react-devtools-content-script') {
              return;
            }
            
            // Security: Strict origin validation
            if (event.origin !== window.location.origin) {
              console.warn('[RegisterPage] SECURITY: Rejected message from untrusted origin:', event.origin);
              return;
            }
            
            // Security: Validate message structure
            if (!event.data?.type || !event.data?.provider || !event.data?.timestamp) {
              console.warn('[RegisterPage] SECURITY: Invalid message structure');
              return;
            }
            
            // Security: Check message age (prevent replay attacks)
            if (event.data.timestamp && typeof event.data.timestamp === 'number') {
              const messageAge = Date.now() - event.data.timestamp;
              if (messageAge > 60000) { // 60 seconds max
                console.warn('[RegisterPage] SECURITY: Message too old, ignoring');
                return;
              }
            } else {
              console.warn('[RegisterPage] SECURITY: Invalid timestamp in message');
              return;
            }
            
            if (event.data.type === 'oauth-success' && event.data.provider === provider) {
              console.log(`[RegisterPage] ${provider} OAuth success - processing`);
              
              // Don't try to close popup due to COOP policy - popup closes itself
              console.log(`[RegisterPage] ${provider} OAuth success - popup will close itself`);
              
              // Force reload to dashboard to get fresh cookies from popup
              console.log(`[RegisterPage] ${provider} OAuth success - redirecting to dashboard`);
              cleanup();
              setIsSocialLoginLoading(null);
              window.location.href = '/dashboard';
            } else if (event.data.type === 'oauth-error') {
              console.log(`[RegisterPage] ${provider} OAuth error:`, event.data.error);
              
              if (popup && !popup.closed) {
                try {
                  popup.close();
                } catch (error) {
                  console.warn(`[RegisterPage] Error popup close failed:`, error);
                }
              }
              
              cleanup();
              setIsSocialLoginLoading(null);
              setError(event.data.error || `${provider} registration failed`);
            }
          };
          
          window.addEventListener('message', handleMessage);
          
          // No server polling needed - using postMessage only
          let pollInterval: NodeJS.Timeout | null = null;
          
          // Cleanup function
          const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (pollInterval) clearInterval(pollInterval);
            if (checkClosed) clearInterval(checkClosed);
          };
          
          // Monitor popup closing and check auth status (All providers)
          let checkClosed: NodeJS.Timeout | null = null;
          
          const monitorPopupClose = () => {
            checkClosed = setInterval(async () => {
              try {
                if (popup && popup.closed) {
                  console.log(`[RegisterPage] 🔍 ${provider} popup closed detected, checking auth status...`);
                  clearInterval(checkClosed!);
                  
                  // Wait for cookies to be available
                  setTimeout(async () => {
                    try {
                      // Check if user is now authenticated
                      await checkAuth();
                      console.log(`[RegisterPage] ✅ ${provider} auth check completed, redirecting...`);
                      cleanup();
                      setIsSocialLoginLoading(null);
                      window.location.href = '/dashboard';
                    } catch (authError) {
                      console.error(`[RegisterPage] ❌ ${provider} auth check failed:`, authError);
                      setIsSocialLoginLoading(null);
                      setError(`${provider} registration failed. Please try again.`);
                    }
                  }, 500); // 500ms delay for cookies
                }
              } catch (error) {
                console.warn(`[RegisterPage] Error checking ${provider} popup status:`, error);
              }
            }, 250); // Check every 250ms
          };
          
          // Start monitoring popup close for all providers
          monitorPopupClose();
        } else {
          throw new Error(`Popup blocked or could not open ${provider} authentication window.`);
        }
        } else {
          throw new Error(response.data.message || `Failed to get ${provider} auth URL.`);
        }
      }
    } catch (err: any) {
      let errorMessage = err.response?.data?.message || err.message || `An error occurred while trying to connect with ${provider}.`;
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
          errorMessage += ` (Details: ${err.response.data.errors.join(', ')})`;
      }
      if (errorMessage.toLowerCase().includes('captcha')) {
          setTurnstileError(errorMessage);
      }
      setError(errorMessage);
      resetTurnstile();
      setIsSocialLoginLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black antialiased">
      <CardSpotlight className="w-full max-w-md">
        <div className="relative z-20">
          <div className="flex justify-center mb-8">
            <img src={clipoidLogo} alt="Clipoid Logo" className="h-14" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-6 text-center">
            Sign Up
          </h1>
          <div className="bg-blue-500/10 border border-blue-500 text-blue-400 px-4 py-3 rounded-md w-full mb-4 text-sm" role="alert">
            <p className="font-semibold">Closed Beta Notice</p>
            <p>We are currently in a closed beta phase. Account creation and login are restricted to authorized test users only. Thank you for your understanding.</p>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-md w-full mb-4 text-sm" role="alert">
              {error}
            </div>
          )}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-black text-muted-foreground">Sign up with</span>
            </div>
          </div>
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => handleSocialLogin('google')}
              className="w-full flex items-center justify-center py-3 px-4 border border-border rounded-lg text-foreground hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary transition duration-150 ease-in-out"
              disabled={isSubmitting || !!isSocialLoginLoading}
            >
              {isSocialLoginLoading === 'google' ? (
                <svg className="animate-spin mr-2 h-5 w-5 text-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <img src="/assets/googleicon.svg" alt="Google Icon" className="mr-2 h-5 w-5" />
              )}
              {isSocialLoginLoading === 'google' ? 'Connecting...' : 'Continue with Google'}
            </button>
            <button
              type="button"
              onClick={() => {}} // Disabled
              className="w-full flex items-center justify-center py-3 px-4 border border-border rounded-lg text-foreground/50 cursor-not-allowed opacity-50"
              disabled={true}
              title="Twitch registration temporarily disabled"
            >
              <img src="/assets/twitchicon.svg" alt="Twitch Icon" className="mr-2 h-5 w-5 grayscale" />
              Continue with Twitch (Disabled)
            </button>
            <button
              type="button"
              onClick={() => {}} // Disabled
              className="w-full flex items-center justify-center py-3 px-4 border border-border rounded-lg text-foreground/50 cursor-not-allowed opacity-50"
              disabled={true}
              title="Kick registration temporarily disabled"
            >
              <img src="/assets/kickicon.svg" alt="Kick Icon" className="mr-2 h-5 w-5 grayscale" />
              Continue with Kick (Disabled)
            </button>
          </div>
          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>By continuing, you agree to Clipoid's{' '}
              <RouterLink to="/terms" target="_blank" className="font-medium text-primary hover:underline">
                Terms of Service
              </RouterLink>.
            </p>
            <p>Read our{' '}
              <RouterLink to="/privacy" target="_blank" className="font-medium text-primary hover:underline">
                Privacy Policy
              </RouterLink>.
            </p>
          </div>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-black text-muted-foreground">Or</span>
            </div>
          </div>
          {!isEmailFormVisible && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsEmailFormVisible(true)}
                className="w-full bg-gray-600 text-gray-400 font-semibold py-3 px-4 rounded-lg shadow-md cursor-not-allowed opacity-60 flex items-center justify-center"
                disabled={true}
              >
                Create Account with Email
              </button>
              <div className="mt-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-3 py-2 rounded-lg text-xs text-center">
                <p className="flex items-center justify-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Email registration is temporarily disabled during beta
                </p>
              </div>
            </div>
          )}
          <div ref={turnstileWidgetRef} className="cf-turnstile-container my-4 h-[65px] flex justify-center"></div>
          {turnstileError && <p className="text-red-400 text-xs mt-1 mb-3 text-center">{turnstileError}</p>}
          {isEmailFormVisible && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="sr-only">Username</label>
                <input
                  type="text"
                  id="username"
                  placeholder="Username"
                  className={`block w-full px-4 py-3 bg-input border ${usernameError ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out`}
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setUsernameError(''); setError(null); }}
                  disabled={isSubmitting}
                />
                {usernameError && <p className="text-red-400 text-xs mt-1">{usernameError}</p>}
              </div>
              <div>
                <label htmlFor="email" className="sr-only">Email Address</label>
                <input
                  type="email"
                  id="email"
                  placeholder="Email Address"
                  className={`block w-full px-4 py-3 bg-input border ${emailError ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); setError(null); }}
                  disabled={isSubmitting}
                />
                {emailError && <p className="text-red-400 text-xs mt-1">{emailError}</p>}
              </div>
              <div>
                <label htmlFor="name" className="sr-only">Full Name (Optional)</label>
                <input
                  type="text"
                  id="name"
                  placeholder="Full Name (Optional)"
                  className="block w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Password</label>
                <input
                  type="password"
                  id="password"
                  placeholder="Password"
                  className={`block w-full px-4 py-3 bg-input border ${passwordError ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); setError(null); }}
                  disabled={isSubmitting}
                />
                {passwordError && <p className="text-red-400 text-xs mt-1">{passwordError}</p>}
              </div>
              <div>
                <label htmlFor="confirmPassword" className="sr-only">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  placeholder="Confirm Password"
                  className={`block w-full px-4 py-3 bg-input border ${confirmPasswordError ? 'border-red-500' : 'border-border'} rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out`}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(''); setError(null); }}
                  disabled={isSubmitting}
                />
                {confirmPasswordError && <p className="text-red-400 text-xs mt-1">{confirmPasswordError}</p>}
              </div>
              <div className="mt-4 text-center text-xs text-muted-foreground">
                <p>By clicking "Sign Up", you agree to Clipoid's{' '}
                  <RouterLink to="/terms" target="_blank" className="font-medium text-primary hover:underline">
                    Terms of Service
                  </RouterLink>.
                </p>
                <p>Read our{' '}
                  <RouterLink to="/privacy" target="_blank" className="font-medium text-primary hover:underline">
                    Privacy Policy
                  </RouterLink>.
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed transition duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center justify-center mt-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing Up...
                  </>
                ) : (
                  'Sign Up'
                )}
              </button>
            </form>
          )}
          <div className="text-center mt-8 text-sm">
            <RouterLink
              to="/login"
              className="px-4 py-2 rounded-md text-primary hover:bg-primary/10 hover:text-primary/80 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary"
            >
              Already have an account? <span className="font-semibold">Login</span>
            </RouterLink>
          </div>
        </div>
      </CardSpotlight>
    </div>
  );
}

export default RegisterPage;